// src/api/profile.js
// Helper functions for fetching and updating a unified user profile
// Uses Supabase RPCs when available, with safe fallbacks.

import { supabase } from '../supabaseClient';

const PROFILE_RPC_ENABLED = process.env.REACT_APP_PROFILE_RPC === 'true';

// Fetch a unified profile: email (auth.users), app username (public.users),
// and basics from public.assessments. Prefers the `get_my_profile` RPC.
export async function getMyProfile() {
  // Try RPC first when explicitly enabled to avoid 404 noise if it's not deployed.
  if (PROFILE_RPC_ENABLED) {
    const rpc = await supabase.rpc('get_my_profile');
    if (!rpc.error && rpc.data) {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth?.user?.id;
        if (!userId) return rpc;
        const { data: appUser } = await supabase
          .from('users')
          .select('current_weight_lbs')
          .eq('id', userId)
          .maybeSingle();
        if (!appUser) return rpc;
        const rows = Array.isArray(rpc.data) ? rpc.data : [];
        if (!rows.length) return rpc;
        return {
          data: [{ ...rows[0], current_weight_lbs: appUser.current_weight_lbs ?? null }, ...rows.slice(1)],
          error: null,
        };
      } catch (_) {
        return rpc; // { data: [...], error: null }
      }
    }
  }

  // Fallback path (if RPC not deployed yet)
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user || null;
    if (!user) return { data: null, error: new Error('Not authenticated') };

    const userId = user.id;
    const email = user.email;

    const [{ data: appUser }, { data: assess }] = await Promise.all([
      supabase.from('users').select('username, current_weight_lbs').eq('id', userId).maybeSingle(),
      supabase.from('assessments').select('name, age, gender, height_feet, height_inches, weight_lbs').eq('user_id', userId).maybeSingle(),
    ]);

    return {
      data: [{
        user_id: userId,
        email,
        username: appUser?.username ?? null,
        current_weight_lbs: appUser?.current_weight_lbs ?? null,
        name: assess?.name ?? null,
        age: assess?.age ?? null,
        gender: assess?.gender ?? null,
        height_feet: assess?.height_feet ?? null,
        height_inches: assess?.height_inches ?? null,
        weight_lbs: assess?.weight_lbs ?? null,
      }],
      error: null,
    };
  } catch (e) {
    return { data: null, error: e };
  }
}

export async function getMyAssessmentReviewStatus() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };

  const { data, error } = await supabase
    .from('assessments')
    .select('reviewed_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return { data: null, error };

  return {
    data: {
      reviewed_at: data?.reviewed_at ?? null,
      has_assessment: !!data,
    },
    error: null,
  };
}

// Update basics. Prefers `update_my_basics` RPC; falls back to updating
// explicit columns on assessments and mirroring into assessment JSON.
export async function updateMyBasics({
  name,
  age,
  gender,
  height_feet,
  height_inches,
  weight_lbs,
}) {
  // Try RPC first when explicitly enabled.
  if (PROFILE_RPC_ENABLED) {
    const rpc = await supabase.rpc('update_my_basics', {
      p_name: name ?? null,
      p_age: age ?? null,
      p_gender: gender ?? null,
      p_height_feet: height_feet ?? null,
      p_height_inches: height_inches ?? null,
      p_weight_lbs: weight_lbs ?? null,
    });
    if (!rpc.error) return rpc; // success
  }

  // Fallback upsert when RPC is not available
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return { data: null, error: new Error('Not authenticated') };

    // Fetch current assessment to merge JSON cleanly
    const { data: current } = await supabase
      .from('assessments')
      .select('assessment')
      .eq('user_id', userId)
      .maybeSingle();

    const prev = current?.assessment ?? {};
    const merged = {
      ...prev,
      basics: {
        ...(prev?.basics || {}),
        name, age, gender,
        height: { feet: height_feet, inches: height_inches },
        weight_lbs,
      },
    };

    const { error } = await supabase
      .from('assessments')
      .upsert({
        user_id: userId,
        name,
        age,
        gender,
        height_feet,
        height_inches,
        weight_lbs,
        assessment: merged,
      }, { onConflict: 'user_id' });
    return { data: { ok: !error }, error };
  } catch (e) {
    return { data: null, error: e };
  }
}

export async function updateMyCurrentWeight(weight, userIdOverride = null) {
  const normalized = Number(weight);
  if (!Number.isFinite(normalized)) return { data: null, error: null };

  const userId = userIdOverride || (await supabase.auth.getUser()).data?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };

  return supabase
    .from('users')
    .upsert({ id: userId, current_weight_lbs: normalized }, { onConflict: 'id' });
}

// Nutrition preferences helpers
export async function getMyNutritionPreferences() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };
  return supabase
    .from('nutrition_preferences')
    .select('proteins, carbs, fats')
    .eq('user_id', userId)
    .maybeSingle();
}

export async function upsertMyNutritionPreferences({ proteins = [], carbs = [], fats = [] }) {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };
  return supabase
    .from('nutrition_preferences')
    .upsert({ user_id: userId, proteins, carbs, fats }, { onConflict: 'user_id' });
}

export default {
  getMyProfile,
  getMyAssessmentReviewStatus,
  updateMyBasics,
  getMyNutritionPreferences,
  upsertMyNutritionPreferences,
  updateMyCurrentWeight,
};

// Add helpers for workout preferences
export async function upsertWorkoutPreferences(preferences = {}) {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };

  // Merge into assessment JSON
  const { data: current } = await supabase
    .from('assessments')
    .select('assessment')
    .eq('user_id', userId)
    .maybeSingle();

  const prev = current?.assessment ?? {};
  const merged = {
    ...prev,
    workout: {
      ...(prev?.workout || {}),
      ...preferences,
    },
  };

  const { data, error } = await supabase
    .from('assessments')
    .upsert({ user_id: userId, assessment: merged }, { onConflict: 'user_id' });
  return { data, error };
}

export async function setMyTrainingDays(training_days) {
  return upsertWorkoutPreferences({ training_days });
}

export async function getMyTrainingDays() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };
  const { data, error } = await supabase
    .from('assessments')
    .select('assessment')
    .eq('user_id', userId)
    .maybeSingle();
  const days = data?.assessment?.workout?.training_days ?? null;
  return { data: days, error };
}

export async function getMyWorkoutPreferences() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) return { data: null, error: new Error('Not authenticated') };
  const { data, error } = await supabase
    .from('assessments')
    .select('assessment')
    .eq('user_id', userId)
    .maybeSingle();
  const assessment = data?.assessment || {};
  const workout = assessment.workout || {};
  const goals = assessment.goals || {};
  const primary_goal = workout.primary_goal || goals.primary || goals.goal_primary || null;
  return { data: { ...workout, primary_goal }, error };
}

// Limitations helpers
export async function setMyWorkoutLimitations(limitations = []) {
  return upsertWorkoutPreferences({ limitations });
}
